import { Example } from "../models/ExampleModel";
import { ExplanationModel } from "../models/ExplanationModel";
import { VerbConjugation } from "../models/VerbConjugationModel";

export const presentTenseExplanation: ExplanationModel = {
  title: "📖 What is Presente?",
  description:
    "The present tense in Spanish is used to express actions that are happening now, habitual actions, general truths, and actions in the near future. Here's a breakdown of its key uses:",
  bulletPoints: [
    "🕒 Actions happening now: The present tense is used for things that are currently happening.",
    "🔁 Habitual actions: It's used for actions that happen regularly or are part of someone's routine.",
    "🌍 General truths: The present tense is used to express facts or truths that are generally accepted.",
    "📅 Near future actions: The present tense can be used to describe actions that are about to happen soon.",
    "💬 Statements and opinions: The present is also used to express statements or opinions about something happening in the moment.",
  ],
};

export const gerundioExplanation: ExplanationModel = {
  title: "Gerundio (Present Participle)",
  description:
    "The gerundio in Spanish is used to express actions in progress, similar to the '-ing' form in English (e.g., 'speaking', 'eating'). It is commonly used with 'estar' to form the present continuous tense (e.g., 'Estoy hablando' - 'I am speaking').",
  bulletPoints: [
    "✅ Regular verbs follow these patterns:",
    "    '-ar' → '-ando' (e.g., hablar → hablando)",
    "    '-er' → '-iendo' (e.g., comer → comiendo)",
    "    '-ir' → '-iendo' (e.g., vivir → viviendo)",
    "⚠️ Some verbs have irregular gerundio forms due to spelling changes or stem modifications.",
    "🔄 Gerundios are often used with 'estar', but also appear with verbs like 'seguir' (continuing) and 'andar' (going around doing something).",
  ],
};

export const gerundioExamples: Example[] = [
  {
    sentence: "Estoy hablando con mi amigo.",
    explanation:
      "🗣️ Ongoing Action – 'I am speaking with my friend.' The gerund is used to describe an action happening at the moment of speaking.",
  },
  {
    sentence: "Sigues leyendo ese libro.",
    explanation:
      "📖 Continuous Action – 'You keep reading that book.' The gerund is used with 'seguir' to indicate an ongoing action.",
  },
  {
    sentence: "Ana está pidiendo ayuda.",
    explanation:
      "🙋 Request in Progress – 'Ana is asking for help.' The gerund is used to show an action happening right now.",
  },
  {
    sentence: "Los niños andan riendo por el parque.",
    explanation:
      "😂 Repeated Action – 'The kids are laughing around the park.' The gerund with 'andar' expresses an action happening repeatedly in different places.",
  },
];

export const regularVerbs: VerbConjugation[] = [
  { verb: "-ar (Hablar)", conjugations: ["hablo", "hablas", "habla", "hablamos", "habláis", "hablan"] },
  { verb: "-er (Comer)", conjugations: ["como", "comes", "come", "comemos", "coméis", "comen"] },
  { verb: "-ir (Vivir)", conjugations: ["vivo", "vives", "vive", "vivimos", "vivís", "viven"] },
];

export const irregularVerbs: VerbConjugation[] = [
  { verb: "Tener", conjugations: ["tengo", "tienes", "tiene", "tenemos", "tenéis", "tienen"] },
  { verb: "Decir", conjugations: ["digo", "dices", "dice", "decimos", "decís", "dicen"] },
  { verb: "Ir", conjugations: ["voy", "vas", "va", "vamos", "vais", "van"] },
  { verb: "Venir", conjugations: ["vengo", "vienes", "viene", "venimos", "venís", "vienen"] },
  { verb: "Oír", conjugations: ["oigo", "oyes", "oye", "oímos", "oís", "oyen"] },
  { verb: "Ser", conjugations: ["soy", "eres", "es", "somos", "sois", "son"] },
  { verb: "Destruir", conjugations: ["destruyo", "destruyes", "destruye", "destruimos", "destruís", "destruyen"] },

];


export const stemChangingVerbsData: ExplanationModel = {
  title: "Stem-Changing (Boot) Verbs",
  description:
    "Some verbs change their stem in all forms except 'nosotros' and 'vosotros'. These changes follow specific patterns:",
  examples: [
    "e → ie: pensar → pienso, piensas, piensa, pensamos, pensáis, piensan",
    "e → i: pedir → pido, pides, pide, pedimos, pedís, piden",
    "o → ue: poder → puedo, puedes, puede, podemos, podéis, pueden",
    "u → ue: jugar → juego, juegas, juega, jugamos, jugáis, juegan",
  ],
};

export const firstPersonIrregularData: ExplanationModel = {
  title: "First-Person Irregular Verbs",
  description:
    "Some verbs have irregular first-person forms but follow regular conjugation for other persons:",
  examples: [
    "Traducir → traduzco",
    "Parecer → parezco",
    "Poner → pongo",
    "Hacer → hago",
    "Salir → salgo",
    "Dar → doy",
    "Saber → sé",
  ],
};

export const irregularGerundiosData: ExplanationModel = {
  title: "Irregular Gerunds",
  description:
    "Some verbs have irregular gerund forms due to spelling changes or stem modifications:",
  examples: [
    "Ir → yendo",
    "Leer → leyendo",
    "Oír → oyendo",
    "Dormir → durmiendo",
    "Pedir → pidiendo",
    "Decir → diciendo",
    "Venir → viniendo",
    "Morir → muriendo",
  ],
};
